import { toast } from "react-toastify";
import { PunchDirection, PunchInOutService } from "../../Common/Services/PunchInOutService";
import React from "react";
import { Redirect } from "react-router-dom";
import { Button, Modal, Spinner } from "react-bootstrap";
import { FaCamera, FaEye, FaEyeSlash, FaRightToBracket } from "react-icons/fa6";
import { Utils } from "../../Common/Utilis";
import "./PunchInOut.scss";
import {
  FaceLandmarker,
  FilesetResolver
} from "@mediapipe/tasks-vision";

const FACE_LANDMARKER_MODEL = "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
const VISION_WASM_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm";

const PUNCH_SESSION_COOKIE = "punchSession";
const PUNCH_STORAGE_KEY = "punchStorage";
const PUNCH_TOKEN_KEY = "punchToken";
export interface PunchBoardUser {
  userUniqueId: number;
  empCode?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string;
  tenantID?: number;
  empid?: number;
  role?: number;
  roleName?: string;
  noofusers?: number;
  todayPunchIn?: string;
  todayPunchOut?: string;
  isNotPunched?: number;
  isPunchedInOnly?: number;
  isCompletedPunch?: number;
  status?: PunchDirection;
  lastPunchTime?: string;
  lastPunchMode?: string;
  locationName?: string;
  profilePicUrl?: string;
}

const getDisplayName = (user: PunchBoardUser) => {
  const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return name || user.userName || "Unknown user";
};

const formatPunchTime = (value?: string) => {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const getUserStatusTone = (user: PunchBoardUser): "in" | "out" | "available" => {
  if (user.isPunchedInOnly === 1) {
    return "in";
  }

  if (user.isCompletedPunch === 1) {
    return "out";
  }

  return "available";
};

const UserList: React.FC<{
  title: string;
  users: PunchBoardUser[];
  tone: "in" | "out";
}> = ({ title, users, tone }) => {
  return (
    <section className={`punch-board-list ${tone}`}>
      <div className="punch-board-list-header">
        <div>
          <h3>{title}</h3>
          <span>{users.length} users</span>
        </div>
      </div>

      <div className="punch-user-list">
        {users.length === 0 && (
          <div className="punch-empty-state">No users to show</div>
        )}

        {users.map((user) => (
          <article
            className={`punch-user-row ${getUserStatusTone(user)}`}
            key={`${tone}-${user.userUniqueId || user.userName}`}
          >
            <div className="punch-avatar" aria-hidden="true">
              {user.profilePicUrl ? (
                <img src={user.profilePicUrl} alt={getDisplayName(user)} />
              ) : (
                getDisplayName(user).charAt(0).toUpperCase()
              )}
            </div>
            <div className="punch-user-main">
              <strong>{getDisplayName(user)}</strong>
              {tone === "in" && user.todayPunchIn && (
                <div className="punch-timer green-timer">
                  In at {formatPunchTime(user.todayPunchIn)}
                </div>
              )}
              {tone === "out" && user.todayPunchOut && (
                <div className="punch-timer red-timer">
                  Out at {formatPunchTime(user.todayPunchOut)}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export const PunchInOut: React.FC = () => {
  const [inUsers, setInUsers] = React.useState<PunchBoardUser[]>([]);
  const [outUsers, setOutUsers] = React.useState<PunchBoardUser[]>([]);
  const [availableUsers, setAvailableUsers] = React.useState<PunchBoardUser[]>([]);
  const [lastUpdated, setLastUpdated] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState<PunchDirection | "">("");
  const [cameraLoading, setCameraLoading] = React.useState(false);
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  // New states for user selection
  const [allUsers, setAllUsers] = React.useState<PunchBoardUser[]>([]);
  const [selectedUser, setSelectedUser] = React.useState<PunchBoardUser | null>(null);
  const [usersLoading, setUsersLoading] = React.useState(false);
  const [showVerificationModal, setShowVerificationModal] = React.useState(false);
  const [passwordEntry, setPasswordEntry] = React.useState("");
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [passwordLoading, setPasswordLoading] = React.useState(false);
  const [liveStatus, setLiveStatus] = React.useState("Waiting for face detection...");
  const [liveStep, setLiveStep] = React.useState(0);
  const [liveReady, setLiveReady] = React.useState(false);
  const faceLandmarkerRef = React.useRef<FaceLandmarker | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const liveSequenceRef = React.useRef({ leftDone: false, rightDone: false, blinkDone: false });
  const blinkBaselineRef = React.useRef<{ left: number; right: number } | null>(null);
  const captureInFlightRef = React.useRef(false);

  const getPunchStorage = (): any => {
    try {
      const stored = localStorage.getItem(PUNCH_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }

      const rawCookie = Utils.getCookie(PUNCH_SESSION_COOKIE);
      if (!rawCookie) {
        return {};
      }

      const session = JSON.parse(rawCookie);
      if (Number(session?.rolId) !== 1 || !session?.userName || !session?.token) {
        return {};
      }

      const punchStorage = {
        user_UniqueID: session.user_UniqueID,
        userName: session.userName,
        tenantID: session.tenantID,
        rolId: session.rolId,
      };

      localStorage.setItem(PUNCH_STORAGE_KEY, JSON.stringify(punchStorage));
      localStorage.setItem(PUNCH_TOKEN_KEY, session.token);
      return punchStorage;
    } catch {
      return {};
    }
  };

  const getPunchToken = (): string => {
    const existingToken = localStorage.getItem(PUNCH_TOKEN_KEY);
    if (existingToken) {
      return existingToken;
    }

    try {
      const rawCookie = Utils.getCookie(PUNCH_SESSION_COOKIE);
      if (!rawCookie) {
        return "";
      }

      const session = JSON.parse(rawCookie);
      if (Number(session?.rolId) !== 1 || !session?.token) {
        return "";
      }

      localStorage.setItem(PUNCH_TOKEN_KEY, session.token);
      return session.token;
    } catch {
      return "";
    }
  };

  const punchStorage = React.useMemo(() => getPunchStorage(), []);
  const punchToken = React.useMemo(() => getPunchToken(), []);
  const isPunchAuthorized = !!punchStorage?.userName && !!punchStorage?.user_UniqueID && !!punchToken;

  const handlePunchLogout = () => {
    localStorage.removeItem(PUNCH_STORAGE_KEY);
    localStorage.removeItem(PUNCH_TOKEN_KEY);
    Utils.setCookie(PUNCH_SESSION_COOKIE, "", 0);
    window.location.href = "/punch-login";
  };

  const loadProfilePictures = React.useCallback(async (users: PunchBoardUser[]) => {
    const updatedUsers = await Promise.all(
      users.map(async (user) => {
        try {
          const profilePicBlob = await PunchInOutService.GetProfilePic({
            userId: user.userUniqueId,
            tenantId: user.tenantID
          });
          
          if (profilePicBlob) {
            const profilePicUrl = URL.createObjectURL(profilePicBlob);
            return { ...user, profilePicUrl };
          }
        } catch (error) {
          console.warn(`Failed to load profile picture for user ${user.userUniqueId}:`, error);
        }
        return user;
      })
    );
    
    setAllUsers(updatedUsers);
    
    const inUsersWithPics = updatedUsers.filter(u => u.isPunchedInOnly === 1);
    const outUsersWithPics = updatedUsers.filter(u => u.isCompletedPunch === 1);
    const availableUsersWithPics = updatedUsers.filter(u => u.isNotPunched === 1);
    console.log("********",outUsersWithPics);
    setInUsers(inUsersWithPics);
    setOutUsers(outUsersWithPics);
    setAvailableUsers(availableUsersWithPics);
  }, []);

  const loadBoard = React.useCallback(() => {
    setLoading(true);
    PunchInOutService.GetPunchBoard()
      .then((board) => {
        setInUsers(board.inUsers || []);
        setOutUsers(board.outUsers || []);
        setAvailableUsers(board.availableUsers || []);
        setAllUsers([...(board.inUsers || []), ...(board.outUsers || []), ...(board.availableUsers || [])]);
        setLastUpdated(board.lastUpdated || new Date().toISOString());
        
        const allUsersList = [...(board.inUsers || []), ...(board.outUsers || []), ...(board.availableUsers || [])];
        loadProfilePictures(allUsersList);
      })
      .catch((err) => {
        console.log(`Server Error, ${err}`);
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (!isPunchAuthorized) {
      return;
    }
    loadBoard();
  }, [loadBoard, isPunchAuthorized]);

  React.useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  React.useEffect(() => {
    if (showVerificationModal) {
      startCamera();
    } else {
      stopStream();
    }
  }, [showVerificationModal]);

  React.useEffect(() => {
    return () => {
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [stream]);


  const getCurrentUserName = (): string | null => {
    try {
      const storage = getPunchStorage();
      return storage?.userName || null;
    } catch {
      return null;
    }
  };

  const currentUser = React.useMemo(() => {
    try {
      return getPunchStorage();
    } catch {
      return {};
    }
  }, []);

  const stopStream = () => {
    stream?.getTracks().forEach((track) => track.stop());
    setStream(null);
  };

  const stopLiveDetection = () => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    faceLandmarkerRef.current = null;
    setLiveReady(false);
  };

  const initFaceLandmarker = async () => {
    if (faceLandmarkerRef.current) {
      return faceLandmarkerRef.current;
    }

    try {
      const vision = await FilesetResolver.forVisionTasks(VISION_WASM_URL);
      const landmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: FACE_LANDMARKER_MODEL },
        runningMode: "VIDEO",
        numFaces: 1,
        outputFaceBlendshapes: true,
      });
      faceLandmarkerRef.current = landmarker;
      setLiveReady(true);
      return landmarker;
    } catch (err) {
      console.error("Failed to initialize face landmarker", err);
      toast.error("Unable to initialize live face detection.");
      return null;
    }
  };

  const startLiveDetection = async (mediaStream: MediaStream) => {
    const landmarker = await initFaceLandmarker();
    if (!landmarker || !videoRef.current) {
      return;
    }

    stopLiveDetection();
    liveSequenceRef.current = { leftDone: false, rightDone: false, blinkDone: false };
    blinkBaselineRef.current = null;
    captureInFlightRef.current = false;
    setLiveStep(0);
    setLiveStatus("Turn left, then turn right, then blink to verify live presence.");

    const video = videoRef.current;
    video.srcObject = mediaStream;
    await video.play().catch(() => undefined);

    const detectFrame = async () => {
      if (!video || !landmarker || video.readyState < 2) {
        animationFrameRef.current = window.requestAnimationFrame(detectFrame);
        return;
      }

      try {
        const result = landmarker.detectForVideo(video, performance.now());
        const landmarks = result.faceLandmarks?.[0] || [];

        if (landmarks.length > 0) {
          const nose = landmarks[1] || landmarks[0];
          const leftEye = landmarks[33] || landmarks[130];
          const rightEye = landmarks[263] || landmarks[359];
          const eyeOpenLeft = Math.hypot((landmarks[159]?.x || 0) - (landmarks[145]?.x || 0), (landmarks[159]?.y || 0) - (landmarks[145]?.y || 0));
          const eyeOpenRight = Math.hypot((landmarks[386]?.x || 0) - (landmarks[374]?.x || 0), (landmarks[386]?.y || 0) - (landmarks[374]?.y || 0));
          const faceCenterX = ((leftEye?.x || 0) + (rightEye?.x || 0)) / 2;
          const faceDrift = Math.abs((nose?.x || 0) - faceCenterX);
          const leftTurn = (nose?.x || 0) > faceCenterX + 0.04;
          const rightTurn = (nose?.x || 0) < faceCenterX - 0.04;

          if (!blinkBaselineRef.current) {
            blinkBaselineRef.current = { left: eyeOpenLeft || 0.03, right: eyeOpenRight || 0.03 };
          }

          const blinkRatio = Math.min(
            (eyeOpenLeft || 0.03) / (blinkBaselineRef.current.left || 0.03),
            (eyeOpenRight || 0.03) / (blinkBaselineRef.current.right || 0.03)
          );
          const blink = blinkRatio < 0.78 && faceDrift < 0.05 && eyeOpenLeft < blinkBaselineRef.current.left * 0.92 && eyeOpenRight < blinkBaselineRef.current.right * 0.92;

          if (!liveSequenceRef.current.leftDone && leftTurn) {
            liveSequenceRef.current.leftDone = true;
            setLiveStep(1);
            setLiveStatus("Great — now turn your head right.");
            return;
          }

          if (liveSequenceRef.current.leftDone && !liveSequenceRef.current.rightDone && rightTurn) {
            liveSequenceRef.current.rightDone = true;
            setLiveStep(2);
            setLiveStatus("Great — now blink once to confirm you are live.");
            return;
          }

          if (liveSequenceRef.current.leftDone && liveSequenceRef.current.rightDone && !liveSequenceRef.current.blinkDone && blink && !captureInFlightRef.current) {
            liveSequenceRef.current.blinkDone = true;
            setLiveStatus("Verification completed. Capturing your image...");
            void captureAndSendLiveImage();
          }
        }
      } catch (error) {
        console.warn("Live detection frame error", error);
      } finally {
        animationFrameRef.current = window.requestAnimationFrame(detectFrame);
      }
    };

    animationFrameRef.current = window.requestAnimationFrame(detectFrame);
  };

  const startCamera = async (): Promise<MediaStream | null> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera access is not supported in this browser.");
      return null;
    }

    stopStream();
    stopLiveDetection();
    setCameraLoading(true);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false
      });
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }

      await startLiveDetection(mediaStream);
      return mediaStream;
    } catch (err) {
      toast.error(`Camera Error, ${err}`);
      return null;
    } finally {
      setCameraLoading(false);
    }
  };

  const waitForVideoReady = async (video: HTMLVideoElement): Promise<boolean> => {
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      return true;
    }

    return new Promise((resolve) => {
      let settled = false;
      const timeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve(video.videoWidth > 0 && video.videoHeight > 0);
      }, 2500);

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        video.removeEventListener("loadedmetadata", handleReady);
        video.removeEventListener("canplay", handleReady);
      };

      const handleReady = () => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve(true);
      };

      video.addEventListener("loadedmetadata", handleReady, { once: true });
      video.addEventListener("canplay", handleReady, { once: true });
    });
  };

  const openVerificationModal = (user: PunchBoardUser) => {
    stopStream();
    setSelectedUser(user);
    setShowVerificationModal(true);
    setPasswordEntry("");
    setPasswordVisible(false);
  };

  const closeVerificationModal = () => {
    setShowVerificationModal(false);
    setSelectedUser(null);
    setPasswordEntry("");
    setPasswordVisible(false);
    stopStream();
  };

  const determinePunchDirection = (user: PunchBoardUser): PunchDirection => {
    if (user.isPunchedInOnly === 1) {
      return "OUT";
    }
    return "IN";
  };

  const requiresPasswordForFace = (user: PunchBoardUser | null) => {
    return !user?.profilePicUrl;
  };

  const getPunchActionLabel = (user: PunchBoardUser | null) => {
    if (!user) {
      return "Punch";
    }

    return determinePunchDirection(user) === "OUT" ? "Punch Out" : "Punch In";
  };

  const verifyPasswordPunch = async (direction: PunchDirection) => {
    if (!selectedUser) {
      toast.error("Please select a user.");
      return false;
    }

    const currentUserName = getCurrentUserName();
    if (!currentUserName) {
      toast.error("User session not available. Please login again.");
      return false;
    }

    if (!passwordEntry) {
      toast.error("Please enter your password.");
      return false;
    }

    setPasswordLoading(true);
    try {
      const result = await PunchInOutService.PunchByPassword(
        direction,
        passwordEntry,
        selectedUser.userName
      );

      if (result?.success) {
        return true;
      }

      toast.error(result?.message || "Punch failed.");
      return false;
    } catch (err: any) {
      toast.error(`Punch failed, ${err}`);
      return false;
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePunchSubmit = async () => {
    if (!selectedUser) {
      toast.error("Please select a user.");
      return;
    }

    const direction = determinePunchDirection(selectedUser);
    const shouldRegisterFaceAfterPassword = direction === "IN" && requiresPasswordForFace(selectedUser);

    if (passwordEntry) {
      const passwordSuccess = await verifyPasswordPunch(direction);
      if (!passwordSuccess) {
        return;
      }

      if (shouldRegisterFaceAfterPassword) {
        const faceRegistered = await registerFaceFromCamera(direction);
        if (faceRegistered) {
          toast.success("Punch saved successfully and face registeration request submitted.");
        } else {
          toast.error("Punch saved successfully, but face registration failed.");
        }
      } else {
        toast.success("Punch saved successfully.");
      }

      loadBoard();
      closeVerificationModal();
      return;
    }

    if (!selectedUser.profilePicUrl) {
      toast.error("No profile picture found. Please use password punch.");
      return;
    }

    const mediaStream = stream || (await startCamera());
    if (!mediaStream) {
      return;
    }

    const video = videoRef.current;
    if (video) {
      const videoReady = await waitForVideoReady(video);
      if (!videoReady) {
        toast.error("Camera is still getting ready. Please try again.");
        return;
      }
    }

    setLiveStatus("Complete the live verification sequence on camera before the punch.");
    toast.info("Follow the on-screen left/right/blink guide to complete verification.");
  };

  const captureCurrentFrameBlob = async (): Promise<Blob | null> => {
    const mediaStream = stream || (await startCamera());
    if (!mediaStream) {
      return null;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      toast.error("Camera is still loading. Please try again.");
      return null;
    }

    const videoReady = await waitForVideoReady(video);
    if (!videoReady) {
      toast.error("Camera is still getting ready. Please try again.");
      return null;
    }

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const context = canvas.getContext("2d");
    context?.drawImage(video, 0, 0, canvas.width, canvas.height);

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
    });
  };

  const captureAndSendLiveImage = async () => {
    if (!selectedUser || captureInFlightRef.current) {
      return;
    }

    captureInFlightRef.current = true;

    const direction = determinePunchDirection(selectedUser);
    const imageBlob = await captureCurrentFrameBlob();
    if (!imageBlob) {
      captureInFlightRef.current = false;
      return;
    }

    setActionLoading(direction);
    try {
      const result = await PunchInOutService.PunchByCapturedImage(
        direction,
        imageBlob,
        selectedUser.userUniqueId,
        selectedUser.tenantID,
        { livenessDecision: "passed" }
      );

      if (result?.success) {
        toast.success(result.message || "Image captured and sent successfully.");
        loadBoard();
        closeVerificationModal();
      } else {
        toast.error(result?.message || "Live face verification failed.");
      }
    } catch (err) {
      toast.error(`Unable to send live face image, ${err}`);
    } finally {
      setActionLoading("");
      captureInFlightRef.current = false;
    }
  };

  const captureImagePunch = (direction: PunchDirection, mediaStream?: MediaStream | null) => {
    if (!selectedUser) {
      toast.error("Please select a user first.");
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      toast.error("Camera is still loading. Please try again.");
      return;
    }

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const context = canvas.getContext("2d");
    context?.drawImage(video, 0, 0, canvas.width, canvas.height);

    if (!video.srcObject && mediaStream) {
      video.srcObject = mediaStream;
    }

    setActionLoading(direction);
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Unable to capture face image.");
        setActionLoading("");
        return;
      }

      PunchInOutService.PunchByCapturedImage(
        direction, 
        blob,
        selectedUser.userUniqueId,
        selectedUser.tenantID
      )
        .then((result) => {
          if (result?.success) {
            toast.success(result.message || "Punch saved successfully.");
            loadBoard();
            closeVerificationModal();
          } else {
            toast.error(result?.message || "Face verification failed.");
          }
        })
        .catch((err) => toast.error(`Unable to send face image, ${err}`))
        .finally(() => setActionLoading(""));
    }, "image/jpeg", 0.92);
  };

  const registerFaceFromCamera = async (direction: PunchDirection) => {
    if (!selectedUser) {
      toast.error("Please select a user first.");
      return false;
    }

    const imageBlob = await captureCurrentFrameBlob();
    if (!imageBlob) {
      return false;
    }

    setActionLoading(direction);
    try {
      const result = await PunchInOutService.PunchByCapturedImage(
        direction,
        imageBlob,
        selectedUser.userUniqueId,
        selectedUser.tenantID
      );

      if (result?.success) {
        return true;
      }

      return false;
    } catch (err: any) {
      return false;
    } finally {
      setActionLoading("");
    }
  };

  const renderedLastUpdated = lastUpdated ? formatPunchTime(lastUpdated) : "--";

  React.useEffect(() => {
    return () => {
      stopLiveDetection();
      stopStream();
    };
  }, []);

  if (!isPunchAuthorized) {
    return <Redirect to="/punch-login" />;
  }

  return (
    <div className="punch-page">
      <div className="punch-toolbar">
        <div className="punch-brand">
          <div>
            <h1>Cimmple</h1>
            <p>Punch In / Out attendance dashboard</p>
          </div>
        </div>
        <Button variant="outline-danger" onClick={handlePunchLogout}>
          Logout
        </Button>
      </div>

      <section className="punch-summary">
        <div>
          <span>Currently In</span>
          <strong>{inUsers.length}</strong>
        </div>
        <div>
          <span>Currently Out</span>
          <strong>{outUsers.length}</strong>
        </div>
      </section>

      {/* User Selection Section */}
      <section className="punch-user-selection">
        <div className="user-selection-header">
          <h2>Select Employee</h2>
          <p>Choose employee to punch in/out</p>
        </div>
        
        {usersLoading ? (
          <div className="user-loading">
            <Spinner animation="border" />
            <span>Loading employees...</span>
          </div>
        ) : (
          <div className="user-selection-grid">
            {allUsers.length === 0 ? (
              <div className="no-users-message">No employees available</div>
            ) : (
              allUsers.map((user) => (
                <div
                  key={user.userUniqueId}
                  className={`user-card ${getUserStatusTone(user)} ${selectedUser?.userUniqueId === user.userUniqueId ? "active" : ""}`}
                  onClick={() => openVerificationModal(user)}
                >
                  <div className="user-card-avatar">
                    {user.profilePicUrl ? (
                      <img src={user.profilePicUrl} alt={getDisplayName(user)} />
                    ) : (
                      <div className="avatar-initial">
                        {getDisplayName(user).charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="user-card-info">
                    <strong>{getDisplayName(user)}</strong>
                    <span>{user.empCode || user.userName || "--"}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </section>

      <Modal show={showVerificationModal} onHide={closeVerificationModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Verify punch for {selectedUser ? getDisplayName(selectedUser) : "user"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedUser && (
            <>
              <div className="punch-verification-meta">
                <div>
                  <strong>{getDisplayName(selectedUser)}</strong>
                  <p>{selectedUser.empCode || selectedUser.userName || "--"}</p>
                </div>
                <div className="verification-actions punch-top-actions">
                  <Button
                    variant={determinePunchDirection(selectedUser) === "OUT" ? "danger" : "success"}
                    onClick={handlePunchSubmit}
                    disabled={passwordLoading || actionLoading !== "" || (!passwordEntry && cameraLoading)}
                  >
                    {(passwordLoading || actionLoading !== "") ? <Spinner animation="border" size="sm" /> : <FaRightToBracket />}
                    {getPunchActionLabel(selectedUser)}
                  </Button>
                </div>
              </div>

              <div className="punch-simple-modal">
              

                <div className="punch-camera-preview">
                  {stream ? (
                    <video ref={videoRef} autoPlay playsInline muted />
                  ) : (
                    <div>
                      <FaCamera />
                      <span>Starting camera...</span>
                    </div>
                  )}
                </div>

                <div className="liveness-status-box" style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: "#fff7e6" }}>
                  <strong>Live test:</strong> {liveStatus}
                  <div style={{ fontSize: 12, color: "#8a5b00", marginTop: 4 }}>
                    Step {liveStep + 1}/3: {liveStep === 0 ? "Turn left" : liveStep === 1 ? "Turn right" : "Blink"}
                  </div>
                </div>

                <div className="verification-password">
                  <label className="password-label">Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      className="form-control"
                      value={passwordEntry}
                      onChange={(e) => setPasswordEntry(e.target.value)}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setPasswordVisible((prev) => !prev)}
                      aria-label={passwordVisible ? "Hide password" : "Show password"}
                    >
                      {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <small className="password-help">
                    {requiresPasswordForFace(selectedUser)
                      ? "No profile picture is available. Use password punch first so face can be registered."
                      : determinePunchDirection(selectedUser) === "OUT"
                        ? "Punch out with password verification or face capture."
                        : "Punch in with password verification or face capture."}
                  </small>
                </div>

                <div className="verification-actions punch-bottom-actions">
                  <Button variant="outline-secondary" onClick={closeVerificationModal}>
                    Cancel
                  </Button>
                </div>

                <canvas ref={canvasRef} className="d-none" />
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <div className="punch-board-grid">
        <UserList title="In Users" users={inUsers} tone="in" />
        <UserList title="Out Users" users={outUsers} tone="out" />
      </div>
    </div>
  );
};

export default PunchInOut;
